/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  BrowserStorage.ts
 */

import {
  ExpirableLRUStorage,
  ExpirableLRUStorageOptions,
} from './ExpirableLRUStorage'
import {
  ExpirableLocalStorage,
  ExpirableLocalStorageOptions,
} from './ExpirableLocalStorage'
import { isLocalStorageAvailable } from './helpers/isLocalStorageAvailable'
import { ExpirableStorage } from './types/ExpirableStorage'

export type BrowserStorageOptions<TValue> = ExpirableLocalStorageOptions &
  ExpirableLRUStorageOptions<TValue>

export class BrowserStorage<TValue = any> implements ExpirableStorage<TValue> {
  protected storage: ExpirableStorage<TValue>

  constructor(options: BrowserStorageOptions<TValue>) {
    if (isLocalStorageAvailable()) {
      this.storage = new ExpirableLocalStorage<TValue>(options)
    } else {
      // TODO: should we choose CookieStorage if cookies are enabled?
      // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/cookieEnabled
      this.storage = new ExpirableLRUStorage<TValue>(options)
    }
  }

  get length() {
    return this.storage.length
  }

  clear() {
    this.storage.clear()
  }

  getExpiresInMs(key: string) {
    return this.storage.getExpiresInMs(key)
  }

  getItem(key: string) {
    return this.storage.getItem(key)
  }

  key(index: number) {
    return this.storage.key(index)
  }

  removeItem(key: string) {
    this.storage.removeItem(key)
  }

  setItem(key: string, value: TValue, ttl?: number) {
    this.storage.setItem(key, value, ttl)
  }
}
